import { ABILITIES, SKILLS, SKILL_KEYS, type Ability, type SkillKey } from '@shared/rules/constants'
import type { ProficiencyLevel } from '@shared/rules/constants'
import {
  ABILITY_FIELDS, PAGE1, PAGE2, SAVE_FIELDS, SKILL_FIELDS, SPELL_ROWS, WEAPON_ROWS,
} from './fieldMap'

/** Field names unique to the 2014 official sheet, used to tell the two apart. */
const LEGACY_FIELDS = {
  name: 'CharacterName',
  classLevel: 'ClassLevel',
  background: 'Background',
  race: 'Race ',
  alignment: 'Alignment',
  xp: 'XP',
  ac: 'AC',
  initiative: 'Initiative',
  speed: 'Speed',
  hpMax: 'HPMax',
  hpCurrent: 'HPCurrent',
  hpTemp: 'HPTemp',
  hitDice: 'HDTotal',
  profBonus: 'ProfBonus',
  passive: 'Passive',
  features: 'Features and Traits',
  equipment: 'Equipment',
  proficiencies: 'ProficienciesLang',
  personality: 'PersonalityTraits ',
  backstory: 'Backstory',
  appearance: 'Features and Traits',
} as const

const LEGACY_ABILITY: Record<Ability, string> = {
  str: 'STR', dex: 'DEX', con: 'CON', int: 'INT', wis: 'WIS', cha: 'CHA',
}

const LEGACY_SKILL: Record<SkillKey, string> = {
  acrobatics: 'Acrobatics', animal_handling: 'Animal', arcana: 'Arcana', athletics: 'Athletics',
  deception: 'Deception ', history: 'History ', insight: 'Insight', intimidation: 'Intimidation',
  investigation: 'Investigation ', medicine: 'Medicine', nature: 'Nature', perception: 'Perception ',
  performance: 'Performance', persuasion: 'Persuasion', religion: 'Religion',
  sleight_of_hand: 'SleightofHand', stealth: 'Stealth ', survival: 'Survival',
}

export interface ImportedCharacter {
  name: string
  className: string
  subclass: string
  species: string
  background: string
  level: number
  alignment: string
  xp: number
  scores: Record<Ability, number>
  hpMax: number
  hpCurrent: number
  hpTemp: number
  armorClass: number
  speed: number
  initiativeBonus: number
  saveProficiencies: Ability[]
  skillProficiencies: Partial<Record<SkillKey, ProficiencyLevel>>
  attacks: { name: string; bonus: string; damage: string; notes: string }[]
  featuresText: string
  speciesTraitsText: string
  featsText: string
  equipmentText: string
  languagesText: string
  toolsText: string
  weaponTrainingText: string
  appearance: string
  backstory: string
  spellNames: string[]
  coins: { cp: number; sp: number; ep: number; gp: number; pp: number }
}

export interface ImportResult {
  source: 'official-2024' | 'official-2014' | 'unrecognised'
  character: ImportedCharacter
  /** Fields the sheet left blank, so the review screen can point at them. */
  missing: string[]
  warnings: string[]
  /** Every readable field, for the "what did you find?" details panel. */
  raw: Record<string, string>
}

const EMPTY: ImportedCharacter = {
  name: '', className: '', subclass: '', species: '', background: '',
  level: 1, alignment: '', xp: 0,
  scores: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
  hpMax: 0, hpCurrent: 0, hpTemp: 0, armorClass: 10, speed: 30, initiativeBonus: 0,
  saveProficiencies: [], skillProficiencies: {}, attacks: [],
  featuresText: '', speciesTraitsText: '', featsText: '', equipmentText: '',
  languagesText: '', toolsText: '', weaponTrainingText: '',
  appearance: '', backstory: '', spellNames: [],
  coins: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 },
}

/**
 * Takes the FIRST signed integer in the text. Stripping every non-digit instead
 * would turn a speed of "30 · fly 30" into 3030, and hand-filled sheets are full
 * of extra text, leading "+", and unicode minus signs.
 */
function parseNumber(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback
  const match = raw.replace(/[−–—]/g, '-').match(/-?\d+/)
  if (!match) return fallback
  const value = Number.parseInt(match[0], 10)
  return Number.isFinite(value) ? value : fallback
}

const abilityModifier = (score: number): number => Math.floor((score - 10) / 2)

/** "Level 5 Rogue", "Rogue 5", "Rogue (Thief) 5" — pull out class and level. */
export function parseClassLevel(raw: string): { className: string; level: number } {
  const level = parseNumber(raw.match(/\b(\d{1,2})\b/)?.[0], 0)
  const className = raw
    .replace(/\blevel\b/i, '')
    .replace(/\b\d{1,2}\b/, '')
    .replace(/[()]/g, ' ')
    .trim()
    .replace(/\s{2,}/g, ' ')
  return { className, level: level > 0 && level <= 20 ? level : 1 }
}

export async function readSheetPdf(file: ArrayBuffer): Promise<ImportResult> {
  // Import the field classes too: bundlers mangle class names, so
  // `constructor.name === 'PDFTextField'` works in Node and silently fails in a
  // built browser bundle. `instanceof` survives minification.
  const { PDFDocument, PDFTextField, PDFCheckBox } = await import('pdf-lib')

  let doc: Awaited<ReturnType<typeof PDFDocument.load>>
  try {
    doc = await PDFDocument.load(file, { ignoreEncryption: true })
  } catch {
    throw new ImportError('That file could not be opened as a PDF.')
  }

  const raw: Record<string, string> = {}
  const checks: Record<string, boolean> = {}

  const form = doc.getForm()
  const fields = form.getFields()

  if (fields.length === 0) {
    throw new ImportError(
      'That PDF has no fillable form fields, so there is nothing to read out of it. ' +
      'Flattened or printed-to-PDF sheets (including D&D Beyond exports) look like a picture to a computer. ' +
      'Use the character builder instead, or re-fill the official sheet and export that.',
    )
  }

  for (const field of fields) {
    const name = field.getName()
    try {
      if (field instanceof PDFTextField) {
        raw[name] = (field.getText() ?? '').trim()
      } else if (field instanceof PDFCheckBox) {
        checks[name] = field.isChecked()
      }
    } catch {
      // A malformed widget should not abort the whole import.
    }
  }

  const looksModern = PAGE1.name in raw && ABILITY_FIELDS.str[1] in raw
  const looksLegacy = LEGACY_FIELDS.name in raw

  if (looksModern) return fromModernSheet(raw, checks)
  if (looksLegacy) return fromLegacySheet(raw, checks)

  throw new ImportError(
    `This PDF has ${fields.length} form fields but none of them match a sheet layout we know. ` +
    'The importer understands the official 2024 and 2014 D&D character sheets.',
  )
}

export class ImportError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ImportError'
  }
}

// ---------- 2024 official sheet ----------

function fromModernSheet(raw: Record<string, string>, checks: Record<string, boolean>): ImportResult {
  const character: ImportedCharacter = { ...EMPTY, scores: { ...EMPTY.scores }, skillProficiencies: {} }
  const missing: string[] = []
  const warnings: string[] = []

  const text = (field: string, label: string, required = false): string => {
    const value = raw[field] ?? ''
    if (!value && required) missing.push(label)
    return value
  }

  character.name = text(PAGE1.name, 'Character name', true)
  character.className = text(PAGE1.className, 'Class', true)
  character.subclass = text(PAGE1.subclass, 'Subclass')
  character.species = text(PAGE1.species, 'Species', true)
  character.background = text(PAGE1.background, 'Background', true)
  character.level = parseNumber(raw[PAGE1.level], 1)
  character.xp = parseNumber(raw[PAGE1.xp], 0)

  character.armorClass = parseNumber(raw[PAGE1.armorClass], 10)
  character.speed = parseNumber(raw[PAGE1.speed], 30)
  character.hpMax = parseNumber(raw[PAGE1.hpMax], 0)
  character.hpCurrent = parseNumber(raw[PAGE1.hpCurrent], character.hpMax)
  character.hpTemp = parseNumber(raw[PAGE1.hpTemp], 0)

  for (const ability of ABILITIES) {
    const [, scoreField] = ABILITY_FIELDS[ability]
    const score = parseNumber(raw[scoreField], 0)
    if (score > 0) character.scores[ability] = score
    else missing.push(`${ability.toUpperCase()} score`)

    if (checks[SAVE_FIELDS[ability][0]]) character.saveProficiencies.push(ability)
  }

  for (const skill of SKILL_KEYS) {
    if (checks[SKILL_FIELDS[skill][0]]) character.skillProficiencies[skill] = 'proficient'
  }

  // The sheet records total initiative; we store only the part added on top of
  // Dexterity, so anything left over (Alert, Harengon) survives the import.
  if (raw[PAGE1.initiative]) {
    character.initiativeBonus =
      parseNumber(raw[PAGE1.initiative], 0) - abilityModifier(character.scores.dex)
  }

  for (const row of WEAPON_ROWS) {
    const name = raw[row[0]] ?? ''
    if (!name) continue
    character.attacks.push({
      name,
      bonus: raw[row[1]] ?? '',
      damage: raw[row[2]] ?? '',
      notes: raw[row[3]] ?? '',
    })
  }

  character.featuresText = [raw[PAGE1.classFeaturesLeft], raw[PAGE1.classFeaturesRight]]
    .filter(Boolean).join('\n\n')
  character.speciesTraitsText = raw[PAGE1.speciesTraits] ?? ''
  character.featsText = raw[PAGE1.feats] ?? ''
  character.weaponTrainingText = raw[PAGE1.weaponProficiencies] ?? ''
  character.toolsText = raw[PAGE1.tools] ?? ''

  character.alignment = raw[PAGE2.alignment] ?? ''
  character.languagesText = raw[PAGE2.languages] ?? ''
  character.equipmentText = raw[PAGE2.equipment] ?? ''
  character.appearance = raw[PAGE2.appearance] ?? ''
  character.backstory = raw[PAGE2.backstory] ?? ''

  for (const row of SPELL_ROWS) {
    const name = raw[row.name] ?? ''
    if (name) character.spellNames.push(name)
  }

  for (const coin of ['cp', 'sp', 'ep', 'gp', 'pp'] as const) {
    character.coins[coin] = parseNumber(raw[PAGE2.coins[coin]], 0)
  }

  if (!character.hpMax) warnings.push('No maximum hit points were filled in.')
  if (character.level < 1) warnings.push('No level was filled in, so it was set to 1.')

  return { source: 'official-2024', character, missing, warnings, raw }
}

// ---------- 2014 official sheet ----------

function fromLegacySheet(raw: Record<string, string>, checks: Record<string, boolean>): ImportResult {
  const character: ImportedCharacter = { ...EMPTY, scores: { ...EMPTY.scores }, skillProficiencies: {} }
  const missing: string[] = []
  const warnings: string[] = [
    'This is the 2014 sheet. Class, species, and background names may not line up exactly with the 2024 rules — check them below.',
  ]

  character.name = raw[LEGACY_FIELDS.name] ?? ''
  if (!character.name) missing.push('Character name')

  const { className, level } = parseClassLevel(raw[LEGACY_FIELDS.classLevel] ?? '')
  character.className = className
  character.level = level
  character.species = raw[LEGACY_FIELDS.race] ?? ''
  character.background = raw[LEGACY_FIELDS.background] ?? ''
  character.alignment = raw[LEGACY_FIELDS.alignment] ?? ''
  character.xp = parseNumber(raw[LEGACY_FIELDS.xp], 0)

  character.armorClass = parseNumber(raw[LEGACY_FIELDS.ac], 10)
  character.speed = parseNumber(raw[LEGACY_FIELDS.speed], 30)
  if (raw[LEGACY_FIELDS.initiative]) {
    character.initiativeBonus =
      parseNumber(raw[LEGACY_FIELDS.initiative], 0) - abilityModifier(parseNumber(raw[LEGACY_ABILITY.dex], 10))
  }
  character.hpMax = parseNumber(raw[LEGACY_FIELDS.hpMax], 0)
  character.hpCurrent = parseNumber(raw[LEGACY_FIELDS.hpCurrent], character.hpMax)
  character.hpTemp = parseNumber(raw[LEGACY_FIELDS.hpTemp], 0)

  for (const ability of ABILITIES) {
    const score = parseNumber(raw[LEGACY_ABILITY[ability]], 0)
    if (score > 0) character.scores[ability] = score
    else missing.push(`${ability.toUpperCase()} score`)
    if (checks[`ST ${LEGACY_ABILITY[ability]}`]) character.saveProficiencies.push(ability)
  }

  for (const skill of SKILL_KEYS) {
    if (checks[LEGACY_SKILL[skill]]) character.skillProficiencies[skill] = 'proficient'
  }

  for (let i = 1; i <= 3; i++) {
    const name = raw[`Wpn Name${i === 1 ? '' : ` ${i}`}`] ?? ''
    if (!name) continue
    character.attacks.push({
      name,
      bonus: raw[`Wpn${i} AtkBonus`] ?? '',
      damage: raw[`Wpn${i} Damage`] ?? '',
      notes: '',
    })
  }

  for (const coin of ['cp', 'sp', 'ep', 'gp', 'pp'] as const) {
    character.coins[coin] = parseNumber(raw[coin.toUpperCase()], 0)
  }

  character.featuresText = raw[LEGACY_FIELDS.features] ?? ''
  character.equipmentText = raw[LEGACY_FIELDS.equipment] ?? ''
  character.languagesText = raw[LEGACY_FIELDS.proficiencies] ?? ''
  character.backstory = [raw[LEGACY_FIELDS.personality], raw[LEGACY_FIELDS.backstory]]
    .filter(Boolean).join('\n\n')

  return { source: 'official-2014', character, missing, warnings, raw }
}

/**
 * Turns a free-text feature box back into named entries. Our own export writes
 * "Name. Body" separated by blank lines, so that round-trips; anything else
 * becomes a single block rather than being mangled.
 */
export function splitNamedText(text: string): { name: string; text: string }[] {
  const blocks = text.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean)
  if (blocks.length === 0) return []

  return blocks.map((block) => {
    const match = block.match(/^([^.\n]{2,80})\.\s+([\s\S]+)$/)
    return match ? { name: match[1]!.trim(), text: match[2]!.trim() } : { name: 'Note', text: block }
  })
}

/** Splits "Common, Sylvan, Elvish" or a newline list into trimmed entries. */
export const splitList = (text: string): string[] =>
  text.split(/[,\n]/).map((v) => v.trim()).filter(Boolean)

export { SKILLS }
